import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, Info } from 'lucide-react'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import type { Cuenta } from '@/types/database'
import type { VistaSaldoClienteConImpuestos } from '@/services/clientesService'
import { useRegistrarPago } from './useRegistrarPago'
import { SaldoAFavorBanner } from './SaldoAFavorBanner'
import { DeudasPendientesList } from './DeudasPendientesList'
import { PagoFormFields } from './PagoFormFields'

interface RegistrarPagoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clienteId?: string
  clienteNombre?: string
  cuentas: Cuenta[]
  clientes?: VistaSaldoClienteConImpuestos[]
  onSuccess?: () => void
}

export function RegistrarPagoDialog({
  open,
  onOpenChange,
  clienteId,
  clienteNombre,
  cuentas,
  clientes = [],
  onSuccess,
}: RegistrarPagoDialogProps) {
  const state = useRegistrarPago({
    open,
    onOpenChange,
    clienteId,
    clienteNombre,
    cuentas,
    clientes,
    onSuccess,
  })

  return (
    <>
    <Sheet open={open} onOpenChange={state.handleIntentarCerrar}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Registrar Pago{state.clienteNombre ? ` - ${state.clienteNombre}` : ''}</SheetTitle>
          <SheetDescription>
            {state.mostrarSelectorCliente && !state.clienteId
              ? 'Selecciona un cliente para ver sus deudas pendientes'
              : 'Selecciona las deudas a pagar o registra un pago a cuenta'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Client selector (only if not pre-selected) */}
          {state.mostrarSelectorCliente && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Cliente *</label>
              <ClienteCombobox
                clientes={clientes}
                value={state.clienteIdSeleccionado}
                onValueChange={state.handleClienteChange}
                placeholder="Buscar cliente..."
              />
            </div>
          )}

          {/* Show rest of form only if client is selected */}
          {!state.clienteId ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Selecciona un cliente para continuar con el registro del pago.
              </AlertDescription>
            </Alert>
          ) : (
            <>
          {/* Toggle pago a cuenta */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="pagoACuenta"
              checked={state.esPagoACuenta}
              onCheckedChange={(checked) => state.handlePagoACuentaChange(checked === true)}
            />
            <label
              htmlFor="pagoACuenta"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Pago a cuenta (anticipo sin deuda específica)
            </label>
          </div>

          {/* Balance in favor banner */}
          {!state.esPagoACuenta && state.totalSaldoAFavor > 0 && (
            <SaldoAFavorBanner
              pagosConSaldo={state.pagosConSaldo}
              totalSaldoAFavor={state.totalSaldoAFavor}
              usarSaldoAFavor={state.usarSaldoAFavor}
              onUsarSaldoChange={state.handleUsarSaldoAFavorChange}
            />
          )}

          {/* Pending debts list */}
          {!state.esPagoACuenta && (
            <DeudasPendientesList
              deudas={state.deudasPendientes}
              seleccionadas={state.deudasSeleccionadas}
              onToggle={state.toggleDeuda}
              onToggleTodas={state.toggleTodas}
              cargando={state.cargandoDeudas}
              error={state.errorDeudas}
              totalSeleccionadas={state.totalDeudasSeleccionadas}
            />
          )}

          {/* Payment form fields (hidden when using balance in favor) */}
          {!state.usarSaldoAFavor && (
            <PagoFormFields
              fechaPago={state.fechaPago}
              onFechaChange={state.setFechaPago}
              montoFormateado={state.montoFormateado}
              onMontoChange={state.handleMontoChange}
              onMontoBlur={state.handleMontoBlur}
              esPagoParcial={state.esPagoParcial}
              esMontoExcedente={state.esMontoExcedente}
              repartoPrevisto={state.repartoPrevisto}
              totalDeudasSeleccionadas={state.totalDeudasSeleccionadas}
              montoNumerico={state.montoNumerico}
              conceptoEditado={state.conceptoEditado}
              conceptoGenerado={state.conceptoGenerado}
              onConceptoChange={state.handleConceptoChange}
              cuentaId={state.cuentaId}
              onCuentaChange={state.setCuentaId}
              cuentas={cuentas}
              observaciones={state.observaciones}
              onObservacionesChange={state.setObservaciones}
              archivoComprobante={state.archivoComprobante}
              onArchivoChange={state.setArchivoComprobante}
              guardando={state.guardando}
            />
          )}

          {/* Factura electrónica del cobro (ORR-255): solo cuando se crea un pago nuevo */}
          {state.puedeFacturar && !state.usarSaldoAFavor && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="emitirFactura"
                  checked={state.emitirFactura}
                  onCheckedChange={(checked) => state.setEmitirFactura(checked === true)}
                />
                <label htmlFor="emitirFactura" className="text-sm font-medium leading-none">
                  Emitir factura electrónica por este cobro
                </label>
              </div>
              {state.emitirFactura && (
                <>
                  <div className="flex items-center space-x-2 pl-6">
                    <Checkbox
                      id="enviarFacturaPorEmail"
                      checked={state.enviarFacturaPorEmail}
                      onCheckedChange={(checked) => state.setEnviarFacturaPorEmail(checked === true)}
                    />
                    <label htmlFor="enviarFacturaPorEmail" className="text-sm leading-none">
                      Enviarla por mail al cliente
                    </label>
                  </div>
                  <p className="pl-6 text-xs text-muted-foreground">
                    Se emite ante ARCA al registrar el pago. Una factura emitida no se borra: se anula con una nota de crédito.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => state.handleIntentarCerrar(false)}
              disabled={state.guardando}
            >
              Cancelar
            </Button>
            <Button
              onClick={state.handleGuardar}
              disabled={!state.puedeGuardar || state.guardando}
            >
              {state.guardando ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {state.usarSaldoAFavor ? 'Aplicando...' : 'Registrando...'}
                </>
              ) : state.usarSaldoAFavor ? (
                'Aplicar Saldo a Favor'
              ) : (
                'Registrar Pago'
              )}
            </Button>
          </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>

    {/* Close confirmation dialog */}
    <AlertDialog open={state.mostrarConfirmacionCerrar} onOpenChange={state.setMostrarConfirmacionCerrar}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Descartar cambios?</AlertDialogTitle>
          <AlertDialogDescription>
            Tienes cambios sin guardar. Si cierras ahora, perderás la información ingresada.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Seguir editando</AlertDialogCancel>
          <AlertDialogAction onClick={state.confirmarCerrar} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Descartar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
